import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import classNames from 'classnames'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { SingleSection } from '../../redux-toolkits/home-page/homePage.type'
import { Status } from '../cards/statusTag'
import Icon from '../icon'
import Dropdown from '../inputs/dropdown'

export const SectionContainer = ({
  children,
  title,
  description,
  onEdit,
  onDisable,
  onDelete,
  showMoreOptions = true,
  childrenClassName = '',
  sortable = false,
  id,
  scrollable = true,
  showSrcollButtons = true,
  section,
  headerExtra,
}: {
  children: ReactNode
  title?: string
  description?: string
  onEdit?: () => void
  onDisable?: () => void
  onDelete?: () => void
  showMoreOptions?: boolean
  childrenClassName?: string
  sortable?: boolean
  id: string
  scrollable?: boolean
  showSrcollButtons?: boolean
  section?: SingleSection
  headerExtra?: ReactNode
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const checkScroll = () => {
    const el = scrollRef.current
    if (!el) return

    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth)
  }

  const scrollBy = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' })
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    checkScroll()
    el.addEventListener('scroll', checkScroll)
    window.addEventListener('resize', checkScroll)

    return () => {
      el.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  }, [])

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 bg-frost border rounded-2xl flex items-center gap-6 "
    >
      {sortable && (
        <button
          className="w-fit cursor-grab !max-w-[50px]"
          {...attributes}
          {...listeners}
          type="button"
        >
          <Icon id="rearrange" width={24} height={24} />
        </button>
      )}

      <div className="flex flex-col gap-6 flex-1 overflow-hidden">
        <div className="flex flex-wrap gap-6 w-full items-center justify-end">
          {title || description || section ? (
            <div className="flex-1 min-w-full md:min-w-fit">
              <div className="flex items-center gap-3">
                {title && (
                  <p className="text-black font-semibold text-xl">{title}</p>
                )}
                {section && (
                  <Status
                    text={section.status}
                    type={section.status === 'PUBLISHED' ? 'success' : 'warn'}
                  />
                )}
              </div>
              {description && (
                <p className="text-sec-black font-normal">{description}</p>
              )}
            </div>
          ) : undefined}

          {scrollable && showSrcollButtons ? (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => {
                  scrollBy(-600)
                }}
                disabled={!canScrollLeft}
                className="disabled:opacity-40"
              >
                <Icon width={24} height={24} id="left-caret-new" />
              </button>

              <button
                type="button"
                onClick={() => {
                  scrollBy(600)
                }}
                disabled={!canScrollRight}
                className="disabled:opacity-30"
              >
                <Icon
                  width={24}
                  height={24}
                  id="left-caret-new"
                  className="rotate-180"
                />
              </button>
            </div>
          ) : undefined}

          {showMoreOptions && (
            <Dropdown
              className="flex-grow md:flex-grow-0 flex justify-end"
              menuButton={
                <button type="button">
                  <Icon width={58} height={58} id="verticalDots" />
                </button>
              }
              menuItems={[
                onEdit
                  ? {
                      name: 'Edit',
                      value: 'edit',
                      onClick: () => {
                        onEdit()
                      },
                    }
                  : undefined,
                onDisable
                  ? {
                      name:
                        section?.status === 'PUBLISHED' ? 'Disable' : 'Enable',
                      value: 'disable',
                      onClick: () => {
                        onDisable()
                      },
                    }
                  : undefined,
                onDelete
                  ? {
                      name: 'Delete',
                      value: 'delete',
                      onClick: () => {
                        onDelete()
                      },
                    }
                  : undefined,
              ]}
            />
          )}

          {headerExtra}
        </div>

        <div
          ref={scrollRef}
          className={classNames(
            `grid max-w-full  gap-6`,
            {
              'grid-flow-col auto-cols-[182px] md:auto-cols-[282px] overflow-x-scroll':
                scrollable,
            },
            childrenClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
